import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/useAuth";
import { CreditsProvider } from "@/hooks/useCredits";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AppLayout } from "@/components/AppLayout";
import Index from "./pages/Index.tsx";
import { ProfileGate } from "@/components/ProfileGate";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { useEffect, lazy, Suspense } from "react";
import { installGlobalErrorHandlers } from "@/lib/errorLogger";
import { startWebVitals } from "@/lib/webVitals";

// Lazy-load all non-homepage routes so the initial bundle stays small.
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Ingredients = lazy(() => import("./pages/Ingredients"));
const Quiz = lazy(() => import("./pages/Quiz"));
const Chat = lazy(() => import("./pages/Chat"));
const Conversations = lazy(() => import("./pages/Conversations"));
const Billing = lazy(() => import("./pages/Billing"));
const SkinAnalysis = lazy(() => import("./pages/SkinAnalysis"));
const RoastShelf = lazy(() => import("./pages/RoastShelf"));
const GlowScore = lazy(() => import("./pages/GlowScore"));
const Referrals = lazy(() => import("./pages/Referrals"));
const GlowUp = lazy(() => import("./pages/GlowUp"));
const Routine = lazy(() => import("./pages/Routine"));
const Account = lazy(() => import("./pages/Account"));
const Feed = lazy(() => import("./pages/Feed"));
const Friends = lazy(() => import("./pages/Friends"));
const UserProfile = lazy(() => import("./pages/UserProfile"));
const Terms = lazy(() => import("./pages/Terms"));
const Disclaimer = lazy(() => import("./pages/Disclaimer"));
const CheckoutReturn = lazy(() => import("./pages/CheckoutReturn"));
const PersonaPublic = lazy(() => import("./pages/PersonaPublic"));
const PersonaCompare = lazy(() => import("./pages/PersonaCompare"));
const RoastPublic = lazy(() => import("./pages/RoastPublic"));
const RoastCompare = lazy(() => import("./pages/RoastCompare"));
const Formulas = lazy(() => import("./pages/Formulas"));
const FormulaPublic = lazy(() => import("./pages/FormulaPublic"));
const AdminCredits = lazy(() => import("./pages/AdminCredits"));
const AdminSystemHealth = lazy(() => import("./pages/AdminSystemHealth"));
const AdminAnalytics = lazy(() => import("./pages/AdminAnalytics"));
const Privacy = lazy(() => import("./pages/Privacy"));
const PickHandle = lazy(() => import("./pages/PickHandle"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const Saved = lazy(() => import("./pages/Saved"));

const queryClient = new QueryClient();

const App = () => {
  useEffect(() => {
    installGlobalErrorHandlers();
    startWebVitals();
  }, []);

  return (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <CreditsProvider>
          <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/p/compare/:a/:b" element={<PersonaCompare />} />
            <Route path="/p/:personaId" element={<PersonaPublic />} />
            <Route path="/r/compare/:a/:b" element={<RoastCompare />} />
            <Route path="/r/:id" element={<RoastPublic />} />
            <Route path="/f/:slug" element={<FormulaPublic />} />
            <Route
              path="/quiz"
              element={
                <ProtectedRoute>
                  <Quiz />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app"
              element={
                <ProtectedRoute>
                  <ProfileGate>
                    <AppLayout />
                  </ProfileGate>
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="welcome/handle" element={<PickHandle />} />
              <Route path="feed" element={<Feed />} />
              <Route path="friends" element={<Friends />} />
              <Route path="u/:username" element={<UserProfile />} />
              <Route path="chat" element={<Chat />} />
              <Route path="conversations" element={<Conversations />} />
              <Route path="ingredients" element={<Ingredients />} />
              <Route path="skin-analysis" element={<SkinAnalysis />} />
              <Route path="roast" element={<RoastShelf />} />
              <Route path="glow" element={<GlowScore />} />
              <Route path="glow-up" element={<GlowUp />} />
              <Route path="routine" element={<Routine />} />
              <Route path="formulas" element={<Formulas />} />
              <Route path="saved" element={<Saved />} />
              <Route path="referrals" element={<Referrals />} />
              <Route path="billing" element={<Billing />} />
              <Route path="account" element={<Account />} />
              <Route path="checkout/return" element={<CheckoutReturn />} />
              <Route path="admin/credits" element={<AdminCredits />} />
              <Route path="admin/system-health" element={<AdminSystemHealth />} />
              <Route path="admin/analytics" element={<AdminAnalytics />} />
            </Route>
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
          </CreditsProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </ErrorBoundary>
  );
};

export default App;
